import React, { useState, useEffect } from 'react';
import { Button, Input, notification, Tag, Tooltip } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, LinkOutlined, ExperimentOutlined, EuroOutlined, TeamOutlined, StarOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import { backendURL } from '../Globals';

const { TextArea } = Input;

const SECTIONS = [
  {
    key: 'concepto',
    label: 'Concepto',
    icon: <ExperimentOutlined />,
    color: '#007398',
    description: '¿Para qué es?',
  },
  {
    key: 'cuantia',
    label: 'Cuantía',
    icon: <EuroOutlined />,
    color: '#2e7d32',
    description: 'Dotación económica',
  },
  {
    key: 'destinatarios',
    label: 'Destinatarios y requisitos',
    icon: <TeamOutlined />,
    color: '#1565c0',
    description: '¿Quién puede solicitarla?',
  },
  {
    key: 'valoracion',
    label: 'Criterios de valoración',
    icon: <StarOutlined />,
    color: '#e65100',
    description: 'Cómo se puntúa',
  },
];

const IIPPIRComponent = () => {
  const [data, setData] = useState({});
  const [editing, setEditing] = useState(null);   // key de la sección en edición
  const [editValue, setEditValue] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    fetch(`${backendURL}/iippir`)
      .then(r => r.json())
      .then(setData)
      .catch(console.error);
    const apiKey = Cookies.get('apiKey') || localStorage.getItem('apiKey');
    setIsLoggedIn(!!apiKey);
  }, []);

  const startEdit = (key) => {
    setEditing(key);
    setEditValue(data[key] || '');
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValue('');
  };

  const saveEdit = async (key) => {
    const apiKey = Cookies.get('apiKey') || localStorage.getItem('apiKey');
    if (!apiKey) { notification.error({ message: 'No estás autenticado' }); return; }
    try {
      const res = await fetch(`${backendURL}/iippir/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({ content: editValue }),
      });
      if (res.ok) {
        setData(prev => ({ ...prev, [key]: editValue }));
        setEditing(null);
        notification.success({ message: 'Guardado correctamente' });
      } else {
        notification.error({ message: 'Error al guardar' });
      }
    } catch {
      notification.error({ message: 'Error de red' });
    }
  };

  const saveEnlace = async () => {
    await saveEdit('enlace');
  };

  // Renderiza texto con saltos de línea respetados
  const renderText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <div>
      {/* Header */}
      <div style={{ paddingTop: 28, paddingBottom: 4, textAlign: 'center', background: '#fafafa' }}>
        <div style={{
          fontSize: 22, fontWeight: 700, color: '#1a1a1a',
          letterSpacing: -0.3, lineHeight: 1.3,
          fontFamily: "'Inter','Segoe UI',sans-serif",
          padding: '0 24px',
        }}>
          Ayuda IIPPIR
        </div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 6, paddingBottom: 16 }}>
          Plan Propio de Investigación 2025-2028 — Ayudas para la difusión de resultados
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '32px auto', padding: '0 24px 56px' }}>

        {/* Tarjetas de secciones */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 20, marginBottom: 28 }}>
          {SECTIONS.map(({ key, label, icon, color, description }) => (
            <div key={key} style={{
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              borderTop: `3px solid ${color}`,
              padding: '22px 22px 18px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18, color }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>{label}</div>
                    <div style={{ fontSize: 11, color: '#aaa' }}>{description}</div>
                  </div>
                </div>
                {isLoggedIn && editing !== key && (
                  <Tooltip title="Editar">
                    <Button
                      type="text" size="small" icon={<EditOutlined />}
                      onClick={() => startEdit(key)}
                      style={{ color: '#aaa' }}
                    />
                  </Tooltip>
                )}
              </div>

              {editing === key ? (
                <div style={{ marginTop: 8 }}>
                  <TextArea
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    autoSize={{ minRows: 4, maxRows: 12 }}
                    style={{ fontSize: 13 }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Button type="primary" size="small" icon={<SaveOutlined />} onClick={() => saveEdit(key)}>Guardar</Button>
                    <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 13.5, color: '#444', lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                  {renderText(data[key]) || <span style={{ color: '#ccc' }}>—</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Enlace a la convocatoria */}
        <div style={{
          background: '#f0f7fa',
          border: '1px solid #c8e4ef',
          borderRadius: 10,
          padding: '18px 22px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#005a7a', marginBottom: 4 }}>
              <LinkOutlined style={{ marginRight: 6 }} />
              Última convocatoria
            </div>
            {editing === 'enlace' ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Input
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  style={{ width: 400, fontSize: 12 }}
                />
                <Button type="primary" size="small" icon={<SaveOutlined />} onClick={saveEnlace}>Guardar</Button>
                <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit}>Cancelar</Button>
              </div>
            ) : (
              <a
                href={data.enlace || '#'}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 13, color: '#007398', wordBreak: 'break-all' }}
              >
                {data.enlace || '—'}
              </a>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isLoggedIn && editing !== 'enlace' && (
              <Tooltip title="Editar enlace">
                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit('enlace')} style={{ color: '#aaa' }} />
              </Tooltip>
            )}
            <a
              href={data.enlace || '#'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button type="primary" icon={<LinkOutlined />} style={{ background: '#007398', borderColor: '#007398' }}>
                Ver convocatoria
              </Button>
            </a>
          </div>
        </div>

        {/* Nota informativa */}
        <div style={{ marginTop: 16, fontSize: 12, color: '#aaa', textAlign: 'center' }}>
          Resolución de 6 de mayo de 2025 de la Vicerrectora de Investigación de la Universidad de Oviedo.
          {' '}Los datos pueden variar en cada convocatoria.
        </div>

      </div>
    </div>
  );
};

export default IIPPIRComponent;
